/**
 * Модальное окно для управления диаграммами: список, сохранение, импорт/экспорт.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Fade,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import FolderOpenOutlinedIcon from '@mui/icons-material/FolderOpenOutlined';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import { useTheme } from '../hooks/useTheme';
import {
  getDiagrams,
  getDiagramById,
  createDiagram,
  updateDiagram,
  deleteDiagram,
} from '../api/diagramsApi';
import { isBackendEnabled } from '../api/systemsApi';

const DOMAINS = [
  'Architecture Vision',
  'Business Architecture',
  'Information Systems Architecture',
  'Technology Architecture',
];

const DIAGRAM_TYPES = [
  { value: 'free', label: 'Free Board' },
  { value: 'uml', label: 'PlantUML / UML' },
  { value: 'c4', label: 'C4 Model' },
  { value: 'archimate', label: 'ArchiMate' },
  { value: 'bpmn', label: 'BPMN 2.0' },
  { value: 'erd', label: 'ERD' },
];

const typeLabel = (value) => DIAGRAM_TYPES.find((type) => type.value === value)?.label || value || 'Диаграмма';

const DiagramModal = ({
  visible,
  onClose,
  elements,
  onLoadDiagram,
  currentDiagramId,
  zoom,
  scrollPosition,
  currentDiagramMeta,
  initialTab,
  code,
  setCode,
  defaultDiagramType = 'uml',
  fixedDiagramType = null,
  extraMeta = null,
  onSave = null,
  onSaved = null,
  projectId = null,
}) => {
  const { theme } = useTheme();
  const isDark = theme.name === 'dark';
  const surface = isDark ? '#151b23' : '#ffffff';
  const surfaceSoft = isDark ? '#111827' : '#f8fafc';
  const border = isDark ? 'rgba(148, 163, 184, 0.2)' : 'rgba(148, 163, 184, 0.28)';
  const mutedText = isDark ? '#94a3b8' : '#64748b';
  const accent = '#2563eb';

  const [tab, setTab] = useState(initialTab || 'list');
  const [diagrams, setDiagrams] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [diagramName, setDiagramName] = useState('');
  const [diagramDomain, setDiagramDomain] = useState(DOMAINS[0]);
  const [diagramType, setDiagramType] = useState(fixedDiagramType || currentDiagramMeta?.type || defaultDiagramType || 'uml');

  const loadDiagramsList = useCallback(async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const list = await getDiagrams(projectId);
      setDiagrams(list);
    } catch (err) {
      setError('Ошибка загрузки списка: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    if (!visible) return;
    if (initialTab && ['list', 'save', 'import'].includes(initialTab)) {
      setTab(initialTab);
    }
  }, [visible, initialTab]);

  useEffect(() => {
    if (!visible || tab !== 'save') return;

    const nextType = fixedDiagramType || currentDiagramMeta?.type || defaultDiagramType || 'uml';
    setDiagramType(nextType);
    setDiagramDomain(
      currentDiagramMeta?.domain && DOMAINS.includes(currentDiagramMeta.domain)
        ? currentDiagramMeta.domain
        : DOMAINS[0],
    );
    setDiagramName(currentDiagramMeta?.name || '');
    setError('');
    setSuccess('');
  }, [visible, tab, currentDiagramId, currentDiagramMeta, defaultDiagramType, fixedDiagramType]);

  useEffect(() => {
    if (visible && tab === 'list') {
      loadDiagramsList();
    }
  }, [visible, tab, loadDiagramsList]);

  const handleSaveDiagram = async () => {
    if (!diagramName.trim()) {
      setError('Введите название диаграммы');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const nextType = fixedDiagramType || diagramType;
      let result;

      if (typeof onSave === 'function') {
        result = await onSave({
          name: diagramName.trim(),
          domain: diagramDomain,
          type: nextType,
        });
      } else {
        const payload = {
          meta: {
            ...(currentDiagramMeta || {}),
            name: diagramName.trim(),
            version: currentDiagramMeta?.version || 1,
            createdAt: currentDiagramMeta?.createdAt || new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            domain: diagramDomain,
            type: nextType,
            code: code || '',
            ...(extraMeta || {}),
          },
          view: {
            zoom: zoom || 1,
            scroll: scrollPosition || { left: 0, top: 0 },
          },
          elements: elements || [],
        };

        result = currentDiagramId
          ? await updateDiagram(projectId, currentDiagramId, payload)
          : await createDiagram(projectId, payload);
      }

      if (typeof onSaved === 'function' && result) {
        onSaved(result);
      }

      setSuccess(currentDiagramId ? 'Диаграмма обновлена' : 'Диаграмма создана');
      setDiagramName(result?.meta?.name || diagramName.trim());
      setDiagramDomain(result?.meta?.domain || diagramDomain);
      await loadDiagramsList();
    } catch (err) {
      setError('Ошибка сохранения: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleLoadDiagram = async (diagramId) => {
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const diagram = await getDiagramById(projectId, diagramId);
      if (diagram.meta?.code && typeof setCode === 'function') {
        setCode(diagram.meta.code);
      }
      onLoadDiagram(diagram, diagramId);
      onClose();
    } catch (err) {
      setError('Ошибка загрузки: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDiagram = async (diagramId, name) => {
    const confirmed = window.confirm(`Удалить диаграмму "${name}"?`);
    if (!confirmed) return;

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await deleteDiagram(projectId, diagramId);
      setSuccess(`Диаграмма "${name}" удалена`);
      await loadDiagramsList();
    } catch (err) {
      setError('Ошибка удаления: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleExportDiagram = () => {
    try {
      const payload = {
        meta: {
          name: diagramName.trim() || 'exported-diagram',
          version: 1,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          domain: diagramDomain,
          type: fixedDiagramType || diagramType,
          ...(extraMeta || {}),
        },
        view: {
          zoom: zoom || 1,
          scroll: scrollPosition || { left: 0, top: 0 },
        },
        elements: elements || [],
      };

      const dataStr = JSON.stringify(payload, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${payload.meta.name}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      setError('Ошибка экспорта: ' + err.message);
    }
  };

  const handleImportDiagram = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const diagram = JSON.parse(e.target.result);
        onLoadDiagram(diagram);
        onClose();
      } catch (err) {
        setError('Ошибка импорта: ' + err.message);
      }
    };
    reader.readAsText(file);
  };

  const primaryButtonSx = {
    borderRadius: '8px',
    border: `1px solid ${accent}`,
    backgroundColor: accent,
    color: '#ffffff',
    fontWeight: 700,
    textTransform: 'none',
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: '#1d4ed8',
      borderColor: '#1d4ed8',
      boxShadow: '0 10px 24px rgba(37, 99, 235, 0.28)',
    },
    '&.Mui-disabled': {
      color: isDark ? 'rgba(255,255,255,0.36)' : 'rgba(15,23,42,0.34)',
    },
  };

  const secondaryButtonSx = {
    borderRadius: '8px',
    borderColor: border,
    color: theme.toolbar.buttonText,
    textTransform: 'none',
    '&:hover': {
      borderColor: accent,
      backgroundColor: isDark ? 'rgba(37, 99, 235, 0.12)' : 'rgba(37, 99, 235, 0.06)',
    },
  };

  const fieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px',
      backgroundColor: surfaceSoft,
      color: theme.contextMenu.text,
      '& fieldset': { borderColor: border },
      '&:hover fieldset': { borderColor: accent },
      '&.Mui-focused fieldset': { borderColor: accent },
    },
    '& .MuiInputLabel-root': { color: mutedText },
    '& .MuiInputLabel-root.Mui-focused': { color: accent },
    '& .MuiSelect-icon': { color: mutedText },
  };

  return (
    <Dialog
      open={!!visible}
      onClose={loading ? undefined : onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '8px',
          border: `1px solid ${border}`,
          background: surface,
          color: theme.contextMenu.text,
          boxShadow: isDark ? '0 24px 80px rgba(0,0,0,0.5)' : '0 24px 80px rgba(15,23,42,0.18)',
          overflow: 'hidden',
        },
      }}
      BackdropProps={{
        sx: {
          backgroundColor: isDark ? 'rgba(2, 6, 23, 0.72)' : 'rgba(15, 23, 42, 0.32)',
          backdropFilter: 'blur(4px)',
        },
      }}
    >
      <DialogTitle sx={{ px: 3, py: 2.25 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800, letterSpacing: 0 }}>
              Управление диаграммами
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', rowGap: 1 }}>
              <Chip size="small" label={typeLabel(fixedDiagramType || diagramType)} sx={{ borderRadius: '8px', color: theme.toolbar.buttonText, borderColor: border }} variant="outlined" />
              {isBackendEnabled() && (
                <Chip size="small" label="Удаленный сервер" sx={{ borderRadius: '8px', color: mutedText, borderColor: border }} variant="outlined" />
              )}
            </Stack>
          </Box>
          <IconButton onClick={onClose} disabled={loading} sx={{ color: mutedText }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <Divider sx={{ borderColor: border }} />

      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ px: 3, pt: 2 }}>
          <Tabs
            value={tab}
            onChange={(_, next) => setTab(next)}
            textColor="inherit"
            TabIndicatorProps={{ sx: { backgroundColor: accent, height: 2 } }}
            sx={{
              minHeight: 40,
              '& .MuiTab-root': {
                minHeight: 40,
                textTransform: 'none',
                color: mutedText,
                fontWeight: 700,
                px: 1.5,
              },
              '& .Mui-selected': { color: theme.contextMenu.text },
            }}
          >
            <Tab value="list" label="Список" icon={<FolderOpenOutlinedIcon fontSize="small" />} iconPosition="start" />
            <Tab value="save" label="Сохранить" icon={<SaveOutlinedIcon fontSize="small" />} iconPosition="start" />
            <Tab value="import" label="Импорт/экспорт" icon={<UploadFileOutlinedIcon fontSize="small" />} iconPosition="start" />
          </Tabs>
        </Box>

        <Box sx={{ p: 3, pt: 2.5 }}>
          {error && <Alert severity="error" sx={{ mb: 2, borderRadius: '8px' }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 2, borderRadius: '8px' }}>{success}</Alert>}

          <Fade in timeout={180} key={tab}>
            <Box>
              {tab === 'list' && (
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, alignItems: 'center', mb: 2 }}>
                    <Box>
                      <Typography sx={{ fontWeight: 800 }}>Сохраненные диаграммы</Typography>
                      <Typography sx={{ color: mutedText, fontSize: 13, mt: 0.25 }}>
                        Загрузите существующую диаграмму или удалите лишнюю запись.
                      </Typography>
                    </Box>
                    <Button startIcon={<RefreshOutlinedIcon />} variant="outlined" sx={secondaryButtonSx} onClick={loadDiagramsList} disabled={loading}>
                      Обновить
                    </Button>
                  </Box>

                  {loading ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, color: mutedText, py: 4 }}>
                      <CircularProgress size={18} />
                      <Typography>Загрузка...</Typography>
                    </Box>
                  ) : diagrams.length === 0 ? (
                    <Paper variant="outlined" sx={{ borderColor: border, borderRadius: '8px', backgroundColor: surfaceSoft, p: 3 }}>
                      <Typography sx={{ color: mutedText }}>Нет сохраненных диаграмм</Typography>
                    </Paper>
                  ) : (
                    <Stack spacing={1.25} sx={{ maxHeight: 360, overflow: 'auto', pr: 0.5 }}>
                      {diagrams.map((diagram) => (
                        <Paper
                          key={diagram.id}
                          variant="outlined"
                          sx={{
                            borderColor: border,
                            borderRadius: '8px',
                            backgroundColor: surfaceSoft,
                            p: 1.5,
                            transition: 'border-color .16s ease, transform .16s ease, box-shadow .16s ease',
                            '&:hover': {
                              borderColor: accent,
                              transform: 'translateY(-1px)',
                              boxShadow: isDark ? '0 14px 32px rgba(0,0,0,0.22)' : '0 14px 32px rgba(15,23,42,0.08)',
                            },
                          }}
                        >
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
                            <Box sx={{ minWidth: 0 }}>
                              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                                <Typography sx={{ fontWeight: 800, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {diagram.meta?.name || 'Без названия'}
                                </Typography>
                                <Chip size="small" label={typeLabel(diagram.meta?.type || 'uml')} sx={{ borderRadius: '8px', height: 22 }} />
                              </Stack>
                              <Typography sx={{ color: mutedText, fontSize: 12 }}>
                                Обновлено: {diagram.meta?.updatedAt ? new Date(diagram.meta.updatedAt).toLocaleString() : 'нет данных'}
                              </Typography>
                            </Box>
                            <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
                              <Button size="small" variant="outlined" sx={secondaryButtonSx} onClick={() => handleLoadDiagram(diagram.id)} disabled={loading}>
                                Загрузить
                              </Button>
                              <IconButton size="small" onClick={() => handleDeleteDiagram(diagram.id, diagram.meta?.name || 'Без названия')} disabled={loading} sx={{ color: '#ef4444' }}>
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            </Stack>
                          </Box>
                        </Paper>
                      ))}
                    </Stack>
                  )}
                </Box>
              )}

              {tab === 'save' && (
                <Stack spacing={2.25}>
                  <Box>
                    <Typography sx={{ fontWeight: 800 }}>Параметры сохранения</Typography>
                    <Typography sx={{ color: mutedText, fontSize: 13, mt: 0.25 }}>
                      Укажите название и архитектурный домен для отображения в пространстве.
                    </Typography>
                  </Box>

                  <TextField
                    label="Название диаграммы"
                    value={diagramName}
                    onChange={(e) => setDiagramName(e.target.value)}
                    fullWidth
                    autoFocus
                    sx={fieldSx}
                  />

                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                    <TextField
                      select
                      label="Домен"
                      value={diagramDomain}
                      onChange={(e) => setDiagramDomain(e.target.value)}
                      fullWidth
                      sx={fieldSx}
                    >
                      {DOMAINS.map((domain) => (
                        <MenuItem key={domain} value={domain}>{domain}</MenuItem>
                      ))}
                    </TextField>

                    {!fixedDiagramType && (
                      <TextField
                        select
                        label="Тип"
                        value={diagramType}
                        onChange={(e) => setDiagramType(e.target.value)}
                        fullWidth
                        sx={fieldSx}
                      >
                        {DIAGRAM_TYPES.map((type) => (
                          <MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>
                        ))}
                      </TextField>
                    )}
                  </Stack>

                  <Box>
                    <Button
                      startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SaveOutlinedIcon />}
                      variant="contained"
                      sx={primaryButtonSx}
                      onClick={handleSaveDiagram}
                      disabled={loading || !diagramName.trim()}
                    >
                      {currentDiagramId ? 'Обновить диаграмму' : 'Создать диаграмму'}
                    </Button>
                  </Box>
                </Stack>
              )}

              {tab === 'import' && (
                <Stack spacing={2.25}>
                  <Box>
                    <Typography sx={{ fontWeight: 800 }}>Импорт и экспорт</Typography>
                    <Typography sx={{ color: mutedText, fontSize: 13, mt: 0.25 }}>
                      Экспортируйте текущую диаграмму в JSON или загрузите диаграмму из файла.
                    </Typography>
                  </Box>

                  <Paper variant="outlined" sx={{ borderColor: border, borderRadius: '8px', backgroundColor: surfaceSoft, p: 2 }}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }}>
                      <TextField
                        label="Название файла"
                        value={diagramName}
                        onChange={(e) => setDiagramName(e.target.value)}
                        fullWidth
                        sx={fieldSx}
                      />
                      <Button startIcon={<DownloadOutlinedIcon />} variant="outlined" sx={{ ...secondaryButtonSx, minWidth: 150 }} onClick={handleExportDiagram}>
                        Экспорт JSON
                      </Button>
                    </Stack>
                  </Paper>

                  <Paper variant="outlined" sx={{ borderColor: border, borderRadius: '8px', backgroundColor: surfaceSoft, p: 2 }}>
                    <Button component="label" startIcon={<UploadFileOutlinedIcon />} variant="outlined" sx={secondaryButtonSx}>
                      Импортировать JSON
                      <input hidden type="file" accept=".json" onChange={handleImportDiagram} />
                    </Button>
                  </Paper>
                </Stack>
              )}
            </Box>
          </Fade>
        </Box>
      </DialogContent>

      <Divider sx={{ borderColor: border }} />

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button variant="outlined" sx={secondaryButtonSx} onClick={onClose} disabled={loading}>
          Закрыть
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DiagramModal;
